import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../../lib/socket';
import { useAuthStore } from '../../store/authStore';
import {
  getThread,
  sendMessage as sendMessageApi,
  type ChatMessageItem,
  type ChatAttachment,
} from '../../services/chatChannelService';
import type { ChatMessage } from '../../services/chatService';
import ChatMessageItem_ from './ChatMessageItem';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import Spinner from '../ui/Spinner';
import { DateSeparator, isSameDay } from './ChatMessageItem';
import { useTypingIndicator } from '../../hooks/useTypingIndicator';

interface ThreadPanelProps {
  channelId: string;
  parentMessageId: string;
  projectId?: string;
  onClose: () => void;
  isArchived?: boolean;
  // CHG-123 REQ-V123-021: 메시지 → 작업/할 일 변환 컨텍스트
  channelType?: 'PROJECT' | 'USER' | 'DM';
  contextProjectKey?: string;
  // CHG-131: 임베드 모드 — 외곽 w-96/border-l/내부 헤더 생략. 부모(예: ChatSidePanel)가 헤더 제공.
  embedded?: boolean;
}

const GROUPING_INTERVAL_MS = 60_000;

export default function ThreadPanel({ channelId, parentMessageId, projectId, onClose, isArchived, channelType, contextProjectKey, embedded }: ThreadPanelProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const [parentMessage, setParentMessage] = useState<ChatMessageItem | null>(null);
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const { typingUsers, handleTyping, stopTyping } = useTypingIndicator(channelId, { threadId: parentMessageId });

  // Load thread
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setMessages([]);
    setParentMessage(null);
    setHasMore(false);
    setNextCursor(null);

    getThread(channelId, parentMessageId)
      .then((data) => {
        if (cancelled) return;
        setParentMessage(data.parentMessage);
        setMessages(data.messages);
        setNextCursor(data.nextCursor);
        setHasMore(!!data.nextCursor);
        setIsLoading(false);
        // Scroll to bottom after loading
        requestAnimationFrame(() => {
          endRef.current?.scrollIntoView();
        });
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [channelId, parentMessageId]);

  // Socket: listen for new thread messages
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleThreadMessage = (data: { parentMessageId: string; message: ChatMessageItem; threadCount: number }) => {
      if (data.parentMessageId !== parentMessageId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === data.message.messageId)) return prev;
        return [...prev, data.message];
      });
      // Update parent threadCount
      setParentMessage((prev) => prev ? { ...prev, threadCount: data.threadCount } : prev);
      // Auto-scroll if at bottom
      if (isAtBottomRef.current) {
        requestAnimationFrame(() => {
          endRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      }
    };

    const handleEdited = (data: { messageId: string; channelId: string; content: string; isEdited: boolean; editedAt: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId
          ? { ...m, content: data.content, isEdited: data.isEdited, editedAt: data.editedAt }
          : m,
      ));
      // Also check parent
      if (parentMessage?.messageId === data.messageId) {
        setParentMessage((prev) => prev ? { ...prev, content: data.content, isEdited: data.isEdited, editedAt: data.editedAt } : prev);
      }
    };

    const handleDeleted = (data: { messageId: string; channelId: string; parentMessageId?: string }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId
          ? { ...m, content: '삭제된 메시지입니다', isDeleted: true, mentions: [], resolvedMentions: [] } as ChatMessageItem
          : m,
      ));
      // If a thread reply was deleted, update parent threadCount
      if (data.parentMessageId === parentMessageId) {
        setParentMessage((prev) => prev ? { ...prev, threadCount: Math.max(0, (prev.threadCount ?? 1) - 1) } : prev);
      }
    };

    const handleReaction = (data: { messageId: string; channelId: string; reactions: unknown[] }) => {
      if (data.channelId !== channelId) return;
      setMessages((prev) => prev.map((m) =>
        m.messageId === data.messageId ? { ...m, reactions: data.reactions } as ChatMessageItem : m,
      ));
    };

    socket.on(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_EDITED, handleEdited);
    socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_DELETED, handleDeleted);
    socket.on(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);

    return () => {
      socket.off(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_EDITED, handleEdited);
      socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_DELETED, handleDeleted);
      socket.off(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);
    };
  }, [channelId, parentMessageId, parentMessage?.messageId]);

  // Scroll detection
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Load more (older replies)
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    getThread(channelId, parentMessageId, nextCursor)
      .then((data) => {
        setMessages((prev) => [...data.messages, ...prev]);
        setNextCursor(data.nextCursor);
        setHasMore(!!data.nextCursor);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [channelId, parentMessageId, nextCursor, hasMore, loadingMore]);

  // Top sentinel for infinite scroll
  const topSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !isLoading) {
          loadMore();
        }
      },
      { root: container, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, isLoading, loadMore]);

  // Send reply
  const handleSend = useCallback(async (content: string, attachments?: ChatAttachment[]) => {
    setSending(true);
    try {
      const msg = await sendMessageApi(channelId, content, attachments, parentMessageId);
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
      // Scroll to bottom
      requestAnimationFrame(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    } finally {
      setSending(false);
    }
  }, [channelId, parentMessageId]);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Participants summary
  const participantText = useMemo(() => {
    const senders = new Map<string, string>();
    for (const msg of messages) {
      if (!msg.isDeleted && !senders.has(msg.sender.userId)) {
        senders.set(msg.sender.userId, msg.sender.name);
      }
    }
    if (senders.size === 0) return null;
    const names = [...senders.values()];
    if (names.length === 1) return `${names[0]}님 참여`;
    return `${names[0]}님 외 ${names.length - 1}명 참여`;
  }, [messages]);

  // Message grouping
  const shouldShowSender = useMemo(() => {
    return messages.map((msg, i) => {
      if (i === 0) return true;
      const prev = messages[i - 1];
      if (prev.sender.userId !== msg.sender.userId) return true;
      if (!isSameDay(prev.createdAt, msg.createdAt)) return true;
      return new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime() > GROUPING_INTERVAL_MS;
    });
  }, [messages]);

  // Map to ChatMessage for ChatMessageItem compatibility
  const mapToChat = (m: ChatMessageItem): ChatMessage => ({
    messageId: m.messageId,
    content: m.content,
    mentions: (m.mentions ?? []) as ChatMessage['mentions'],
    resolvedMentions: m.resolvedMentions as ChatMessage['resolvedMentions'],
    reactions: m.reactions ?? [],
    attachments: m.attachments ?? [],
    isEdited: m.isEdited ?? false,
    isDeleted: m.isDeleted ?? false,
    editedAt: m.editedAt ?? null,
    createdAt: m.createdAt,
    sender: m.sender,
  });

  // CHG-131: embedded면 외곽 w-96/border-l + 내부 헤더 생략 (부모가 헤더 제공)
  const outerClass = embedded ? 'flex flex-col h-full min-h-0' : 'w-96 border-l bg-white flex flex-col h-full';

  if (isLoading) {
    return (
      <div className={outerClass}>
        {!embedded && (
          <div className="h-14 flex items-center justify-between px-4 border-b shrink-0">
            <h3 className="font-semibold text-lg">스레드</h3>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
              <X className="size-5" />
            </button>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <Spinner className="size-6" />
        </div>
      </div>
    );
  }

  return (
    <div className={outerClass}>
      {/* Header — h-14 matches ChatChannelHeader. embedded 모드에서는 부모가 헤더 제공. */}
      {!embedded && (
        <div className="h-14 flex items-center justify-between px-4 border-b shrink-0">
          <h3 className="font-semibold text-lg">스레드</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
            <X className="size-5" />
          </button>
        </div>
      )}

      {/* Parent message */}
      {parentMessage && (
        <div className="px-4 py-3 border-b bg-gray-50 shrink-0">
          <ChatMessageItem_
            message={mapToChat(parentMessage)}
            isMine={parentMessage.sender.userId === userId}
            showSender
            isThreadRoot
            channelId={channelId}
            currentUserId={userId}
            channelType={channelType}
            contextProjectId={projectId}
            contextProjectKey={contextProjectKey}
          />
        </div>
      )}

      {/* Participants summary */}
      {participantText && (
        <div className="px-4 py-2 text-sm text-gray-500 border-b shrink-0">
          {participantText}
        </div>
      )}

      {/* Reply list — scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2 min-h-0">
        <div ref={topSentinelRef} className="h-1" />

        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner className="size-4" />
          </div>
        )}

        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <p className="text-sm">아직 답글이 없습니다.</p>
            <p className="text-sm mt-1">첫 답글을 남겨보세요!</p>
          </div>
        )}

        {messages.map((msg, i) => {
          const isMine = msg.sender.userId === userId;
          const showDate = i === 0 || !isSameDay(messages[i - 1].createdAt, msg.createdAt);
          const showSender = shouldShowSender[i];

          return (
            <div key={msg.messageId} id={`thread-msg-${msg.messageId}`}>
              {showDate && <DateSeparator date={msg.createdAt} />}
              <ChatMessageItem_
                message={mapToChat(msg)}
                isMine={isMine}
                showSender={showSender}
                isThreadReply
                channelId={channelId}
                currentUserId={userId}
                compact
                channelType={channelType}
                contextProjectId={projectId}
                contextProjectKey={contextProjectKey}
              />
            </div>
          );
        })}

        <div ref={endRef} />
      </div>

      {/* Typing indicator */}
      <TypingIndicator typingUsers={typingUsers} />

      {/* Reply input */}
      {isArchived ? (
        <div className="px-4 py-3 bg-yellow-50 border-t text-sm text-yellow-700 shrink-0">
          📦 아카이브된 채널에서는 답글을 작성할 수 없습니다.
        </div>
      ) : (
        <div className="shrink-0">
          <ChatInput
            projectId={projectId ?? ''}
            channelId={channelId}
            onSend={handleSend}
            sending={sending}
            compact
            placeholder="답글 입력..."
            onTyping={handleTyping}
            onStopTyping={stopTyping}
          />
        </div>
      )}
    </div>
  );
}
